import { Link } from "react-router";
import { useState } from "react";
import { ArrowRight, ChevronRight, FileText, Layers, SquareCheckBig, Terminal } from "lucide-react";

/**
 * Public landing page — shown to unauthenticated visitors at /.
 * Landing page.
 *   - fixed nav with backdrop blur, Services dropdown, Sign In → /login
 *   - hero with grid bg pattern, blur orb, badge, stats
 *   - features (3 cards), modules (pills), how-it-works (3 steps), about
 *   - footer
 *
 * The only deviation from the HTML spec: the QC badge in nav/footer is
 * replaced with the real Tailseed logo at /logo.png.
 */
export function LandingPage() {
  const [servicesOpen, setServicesOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* ── Fixed nav ─────────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-gray-200/80 bg-white/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Tailseed" className="w-7 h-7 rounded-sm object-contain" />
            <span className="text-gray-900 font-semibold text-sm tracking-tight">Tailseed</span>
          </Link>

          {/* Nav links */}
          <div className="flex items-center gap-6">
            {/* Services dropdown */}
            <div
              className="relative hidden sm:block"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <span className="text-sm text-gray-500 hover:text-gray-900 transition-colors cursor-default">
                Services
              </span>
              {servicesOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
                  <div className="rounded-lg border border-gray-200 bg-white shadow-md p-2 w-56">
                    <div className="px-3 py-2 text-sm text-gray-700">Implementation support</div>
                    <div className="px-3 py-2 text-sm text-gray-700">Configuration audits</div>
                    <div className="px-3 py-2 text-sm text-gray-700">Training and enablement</div>
                  </div>
                </div>
              )}
            </div>
            <a
              href="#pricing"
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors hidden sm:block"
            >
              Pricing
            </a>
            <a
              href="#about"
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors hidden sm:block"
            >
              About
            </a>
            <Link to="/login">
              <button className="inline-flex items-center justify-center whitespace-nowrap bg-blue-500 text-white hover:bg-blue-600 rounded-md px-3 h-8 text-xs gap-1.5 font-semibold transition-colors">
                Sign In <ChevronRight className="w-3.5 h-3.5" aria-hidden />
              </button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative pt-14 bg-gray-50 overflow-hidden">
        {/* Grid background */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgb(0,0,0) 1px, transparent 1px), linear-gradient(rgb(0,0,0) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        {/* Blur orb */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-blue-500/8 rounded-full blur-[140px] pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6 py-28 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white text-gray-500 text-xs font-medium mb-8 shadow-sm">
            <Layers className="w-3.5 h-3.5 text-blue-500" aria-hidden />
            Cloud Transformation Accelerator
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 tracking-tight leading-[1.1] mb-6">
            Complete your Oracle Cloud
            <br />
            <span className="text-blue-500">HCM tasks faster</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed mb-10">
            Generate requirements documents, HDL configuration files, and unit test cases from expert-guided
            questions — in seconds. Made for Oracle HCM consultants who deliver on schedule.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-16">
            <Link to="/register">
              <button className="inline-flex items-center justify-center whitespace-nowrap text-sm bg-blue-500 text-white hover:bg-blue-600 transition-colors h-10 rounded-md gap-2 font-semibold px-8">
                Get Started <ArrowRight className="w-4 h-4" aria-hidden />
              </button>
            </Link>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-center">
            {[
              { value: "13+", label: "HCM Tasks" },
              { value: "4", label: "Modules" },
              { value: "3", label: "Document Types" },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl font-bold text-gray-900 font-mono">{stat.value}</div>
                <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────────────────── */}
      <section className="bg-white border-t border-gray-100 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Everything you need to deliver
            </h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              Answer a set of expert-curated questions and get three production-quality deliverables
              instantly.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <FileText className="w-5 h-5" aria-hidden />,
                iconBg: "bg-blue-500/10 text-blue-500",
                title: "Requirements Documents",
                desc: "Structured requirement docs with functional specs, acceptance criteria, and stakeholder sign-off tables — ready for the client in seconds.",
              },
              {
                icon: <Terminal className="w-5 h-5" aria-hidden />,
                iconBg: "bg-blue-500/10 text-blue-500",
                title: "HDL Configuration Files",
                desc: "Production-ready Oracle HDL data files in the correct format, structured for direct load into your Oracle HCM environment.",
              },
              {
                icon: <SquareCheckBig className="w-5 h-5" aria-hidden />,
                iconBg: "bg-emerald-500/10 text-emerald-500",
                title: "Unit Test Cases",
                desc: "Step-by-step test scenarios with pre-conditions, expected results, and pass/fail tracking. Nothing ships without being tested.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="rounded-xl border border-gray-200 bg-white p-6 hover:border-gray-300 hover:shadow-md transition-all duration-200"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${card.iconBg}`}>
                  {card.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{card.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Modules ───────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 border-t border-gray-100 py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-6">
            Covering all major HCM modules
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {["Core HR", "Absence Management", "Benefits", "Payroll"].map((mod) => (
              <span
                key={mod}
                className="px-4 py-1.5 rounded-full border border-gray-200 text-sm text-gray-600 bg-white shadow-sm"
              >
                {mod}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────────── */}
      <section className="bg-white border-t border-gray-100 py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              From task to deliverable in three steps
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Select a task",
                desc: "Browse the task library and pick the Oracle HCM configuration you need to document.",
              },
              {
                step: "02",
                title: "Answer the questions",
                desc: "Each question has a hint explaining what Oracle needs and why. No guessing.",
              },
              {
                step: "03",
                title: "Get your documents",
                desc: "Requirements, HDL config, and test cases are generated instantly and ready to copy.",
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="text-4xl font-bold font-mono text-blue-500/25 mb-4">{item.step}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About ─────────────────────────────────────────────────────────── */}
      <section id="about" className="bg-gray-50 border-t border-gray-100 py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Built for Oracle HCM consultants
          </h2>
          <p className="text-gray-500 leading-relaxed mb-4 max-w-2xl mx-auto">
            Cloud transformations require precise configuration, thorough documentation, and rigorous testing.
            Tailseed captures the expertise of experienced consultants into a guided workflow — so every
            engagement starts with a solid foundation.
          </p>
          <p className="text-gray-500 leading-relaxed max-w-2xl mx-auto">
            Whether you&apos;re configuring Core HR, setting up Absence rules, building Benefits programs, or
            mapping Payroll elements, the task library walks you through every decision point and produces
            client-ready artifacts automatically.
          </p>
          <div className="mt-10">
            <Link to="/register">
              <button className="inline-flex items-center justify-center whitespace-nowrap text-sm bg-blue-500 text-white hover:bg-blue-600 transition-colors h-10 rounded-md gap-2 font-semibold px-8">
                Create your account <ArrowRight className="w-4 h-4" aria-hidden />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer className="bg-white border-t border-gray-100 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Tailseed" className="w-5 h-5 rounded-sm object-contain" />
            <span className="text-sm text-gray-400">Tailseed</span>
          </div>
          <p className="text-xs text-gray-300">© 2026 Tailseed. Cloud Transformation Accelerator.</p>
        </div>
      </footer>
    </div>
  );
}
